import React, { useState, useEffect } from 'react';
import { Typography, Alert, Button } from 'antd';
import { FireOutlined, RightOutlined } from '@ant-design/icons';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import auctionService from '../../services/auctionService';
import { toggleFavoriteAuction } from '../../redux/userSlice';
import CompactAuctionCard from './CompactAuctionCard';
import CompactAuctionCardSkeleton from './CompactAuctionCardSkeleton';
import { message } from 'antd';
import './FeaturedActiveAuctions.css';

const { Title, Text } = Typography;

const FeaturedActiveAuctions = ({ limit = 10, showViewAll = true, category = null, excludeAuctionId = null, hideIfEmpty = false }) => {
    const [auctions, setAuctions] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const { currentUser } = useSelector((state) => state.user);

    useEffect(() => {
        fetchFeaturedActiveAuctions();
    }, [category, limit]);

    // Sync local state with Redux favorites
    useEffect(() => {
        if (currentUser) {
            const favoriteAuctions = currentUser.favorite_auctions || [];
            setAuctions(prevAuctions => 
                prevAuctions.map(auction => ({
                    ...auction,
                    is_favorited: favoriteAuctions.includes(auction.id)
                }))
            );
        }
    }, [currentUser?.favorite_auctions]);

    const fetchFeaturedActiveAuctions = async () => {
        try {
            setLoading(true);
            console.log('Fetching featured active auctions...', { category, excludeAuctionId });
            
            // Build query parameters
            const params = {
                featured: true,
                per_page: limit
            };
            
            // Add category filter if provided
            if (category) {
                // Support both category object with id/slug/name and direct id/slug/name
                if (typeof category === 'object' && category !== null) {
                    // Prefer slug, then name, then id
                    if (category.slug) {
                        params.category = category.slug;
                    } else if (category.name) {
                        params.category = category.name;
                    } else if (category.id) {
                        // If only id is available, we'll need to use category_id
                        // But backend uses 'category' for name search, so we'll pass the id as category
                        // This might need backend support, but let's try name first
                        params.category = category.id;
                    }
                } else if (typeof category === 'string' || typeof category === 'number') {
                    params.category = category;
                }
            }
            
            // Try multiple approaches to get featured auctions
            let response;
            
            // Get featured auctions
            try {
                response = await auctionService.getAllAuctions(params);
                console.log('Featured auctions API response:', response);
                console.log('Response structure:', {
                    hasData: !!response?.data,
                    isArray: Array.isArray(response),
                    dataType: typeof response?.data,
                    dataLength: response?.data?.length,
                    fullResponse: response
                });
            } catch (error) {
                console.error('Failed to get featured auctions:', error);
                console.error('Error details:', error.response?.data || error.message);
                // Fallback: try with 'active' status
                try {
                    response = await auctionService.getAllAuctions({
                        ...params,
                        status: 'active'
                    });
                    console.log('Featured active auctions (fallback):', response);
                } catch (error2) {
                    console.error('Failed to get featured active auctions (fallback):', error2);
                    console.error('Fallback error details:', error2.response?.data || error2.message);
                }
            }
            
            // Handle paginated response structure
            // Laravel Resource Collection returns: { data: [...], links: {...}, meta: {...} }
            let auctionsData = [];
            if (response) {
                if (Array.isArray(response)) {
                    // Direct array response
                    auctionsData = response;
                } else if (response.data && Array.isArray(response.data)) {
                    // Paginated response with data property
                    auctionsData = response.data;
                } else if (Array.isArray(response)) {
                    auctionsData = response;
                }
            }
            
            if (auctionsData && auctionsData.length > 0) {
                // Filter out excluded auction if provided
                let filteredAuctions = auctionsData;
                if (excludeAuctionId) {
                    filteredAuctions = filteredAuctions.filter(auction => auction.id !== excludeAuctionId);
                }
                
                // Process auctions with favorites
                const favoriteAuctions = currentUser?.favorite_auctions || [];
                const auctionsWithFavorites = filteredAuctions.map(auction => ({
                    ...auction,
                    is_favorited: favoriteAuctions.includes(auction.id)
                }));
                
                console.log('Processed auctions:', auctionsWithFavorites);
                setAuctions(auctionsWithFavorites);
                setError(null);
            } else {
                console.log('No auctions found in response', response);
                setAuctions([]);
                setError(null);
            }
        } catch (error) {
            console.error('Error fetching featured active auctions:', error);
            setError('Failed to load featured auctions. Please try again later.');
        } finally {
            setLoading(false);
        }
    };

    const handleFavoriteToggle = (auctionId) => {
        // Optimistically update local state
        setAuctions(prevAuctions => 
            prevAuctions.map(auction => 
                auction.id === auctionId 
                    ? { ...auction, is_favorited: !auction.is_favorited }
                    : auction
            )
        );

        // Optimistically update Redux state
        dispatch(toggleFavoriteAuction(auctionId));
    };

    if (error) {
        return (
            <Alert
                message="Error"
                description={error}
                type="error"
                showIcon
                style={{ marginBottom: 24 }}
            />
        );
    }

    // Hide component if no auctions found and hideIfEmpty is true
    if (!loading && auctions.length === 0 && hideIfEmpty) {
        return null;
    }

    if (!loading && auctions.length === 0) {
        return (
            <div style={{ marginBottom: 40 }}>
                <div style={{ 
                    marginBottom: 24,
                    padding: '24px',
                    background: 'linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)',
                    borderRadius: '16px',
                    border: '1px solid #dee2e6'
                }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 8 }}>
                        <FireOutlined style={{ 
                            fontSize: '24px', 
                            color: '#6c757d' 
                        }} />
                        <Title level={3} style={{ 
                            margin: 0, 
                            color: '#6c757d',
                            fontWeight: 600,
                            fontSize: '20px'
                        }}>
                            No Featured Live Auctions
                        </Title>
                    </div>
                    <Text style={{ 
                        fontSize: '14px',
                        color: '#6c757d',
                        fontWeight: 400
                    }}>
                        There are currently no featured live auctions. Check back later for exciting auctions!
                    </Text>
                </div>
            </div>
        );
    }

    return (
        <div style={{ marginBottom: 40 }}>
            {/* Header */}
            <div style={{ 
                marginBottom: 24,
                padding: window.innerWidth <= 768 ? '16px' : '24px',
                background: 'linear-gradient(135deg, #ffd700 0%, #ffed4e 100%)',
                borderRadius: '16px',
                boxShadow: '0 8px 24px rgba(255, 215, 0, 0.2)',
                position: 'relative',
                overflow: 'hidden'
            }}>
                {/* Background decoration */}
                <div style={{
                    position: 'absolute',
                    top: '-50%',
                    right: '-50%',
                    width: '200%',
                    height: '200%',
                    background: 'radial-gradient(circle, rgba(255, 215, 0, 0.1) 0%, transparent 70%)',
                    animation: 'float 6s ease-in-out infinite'
                }} />
                
                <div style={{ position: 'relative', zIndex: 2 }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12, marginBottom: 8 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: window.innerWidth <= 768 ? 8 : 12 }}>
                        <FireOutlined style={{ 
                            fontSize: window.innerWidth <= 768 ? '20px' : '24px', 
                            color: '#1f2937',
                            flexShrink: 0
                        }} />
                        <Title level={3} style={{ 
                            margin: 0, 
                            color: '#1f2937',
                            fontWeight: 800,
                            fontSize: window.innerWidth <= 576 ? '18px' : window.innerWidth <= 768 ? '20px' : '24px',
                            lineHeight: 1.2
                        }}>
                            Featured Live Auctions
                        </Title>
                        </div>
                        {showViewAll && (
                            <Button 
                                type="primary"
                                icon={<RightOutlined />}
                                onClick={() => navigate('/featured-auctions')}
                                style={{
                                    fontSize: window.innerWidth <= 576 ? '12px' : window.innerWidth <= 768 ? '13px' : '14px',
                                    fontWeight: 600,
                                    height: window.innerWidth <= 576 ? '36px' : window.innerWidth <= 768 ? '38px' : '40px',
                                    padding: window.innerWidth <= 576 ? '0 16px' : '0 20px',
                                    borderRadius: '10px',
                                    background: 'linear-gradient(135deg, #1f2937 0%, #374151 100%)',
                                    border: 'none',
                                    boxShadow: '0 4px 12px rgba(31, 41, 55, 0.3)',
                                    transition: 'all 0.3s ease',
                                    whiteSpace: 'nowrap',
                                    marginBottom: window.innerWidth <= 576 ? '8px' : '0'
                                }}
                                onMouseEnter={(e) => {
                                    e.currentTarget.style.transform = 'translateY(-2px)';
                                    e.currentTarget.style.boxShadow = '0 6px 16px rgba(31, 41, 55, 0.4)';
                                }}
                                onMouseLeave={(e) => {
                                    e.currentTarget.style.transform = 'translateY(0)';
                                    e.currentTarget.style.boxShadow = '0 4px 12px rgba(31, 41, 55, 0.3)';
                                }}
                            >
                                View all Featured Auctions
                            </Button>
                        )}
                    </div>
                    <Text style={{ 
                        fontSize: window.innerWidth <= 576 ? '13px' : window.innerWidth <= 768 ? '14px' : '16px',
                        color: '#374151',
                        fontWeight: 500,
                        lineHeight: 1.5
                    }}>
                        Discover the most exciting live auctions happening right now
                    </Text>
                </div>
            </div>

            {/* Horizontal scrollable row */}
            <div
                className="featured-auctions-scroll"
                style={{
                    display: 'flex',
                    overflowX: 'auto',
                    gap: 16,
                    paddingBottom: 8,
                    scrollbarWidth: 'thin',
                }}
            >
                {loading
                    ? Array.from({ length: 10 }).map((_, idx) => (
                        <div key={idx} style={{ flex: '0 0 240px', minWidth: 240 }}>
                            <CompactAuctionCardSkeleton />
                        </div>
                    ))
                    : auctions.map((auction) => (
                        <div key={auction.id} style={{ flex: '0 0 240px', minWidth: 240 }}>
                            <CompactAuctionCard auction={auction} onFavoriteToggle={handleFavoriteToggle} />
                        </div>
                    ))}
            </div>

            <style jsx>{`
                @keyframes float {
                    0%, 100% { transform: translateY(0px) rotate(0deg); }
                    50% { transform: translateY(-10px) rotate(180deg); }
                }
            `}</style>
        </div>
    );
};

export default FeaturedActiveAuctions; 